import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Send, X, MessageCircle, ArrowDown } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { toast } from 'sonner';

export default function ChatWindow({ booking, onClose }) {
  const { user } = useAuthStore();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionDate, setActionDate] = useState('');
  const messagesEndRef = useRef(null);
  const prevMsgCountRef = useRef(0);
  const [unseen, setUnseen] = useState(0);
  const isTeamUser = ['admin', 'owner', 'overseer'].includes(user?.role);

  const doBookingAction = async (action, extra = {}) => {
    try {
      await apiClient.post(`/admin/booking-chats/${booking.booking_id}/action`, { action, ...extra });
      toast.success(action === 'confirm' ? '✅ Booking confirmed!' : action === 'cancel' ? 'Booking cancelled' : '📅 Date updated');
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await apiClient.get(`/messages/booking/${booking.booking_id}`);
      setMessages(response.data);
    } catch (error) {
      // Silent fail for message polling
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [booking.booking_id]);

  useEffect(() => {
    const box = messagesEndRef.current?.parentElement;
    if (!box) return;
    const firstLoad = prevMsgCountRef.current === 0;
    const added = messages.length - prevMsgCountRef.current;
    prevMsgCountRef.current = messages.length;
    const nearBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 120;
    if (nearBottom || firstLoad) {
      box.scrollTop = box.scrollHeight;
      setUnseen(0);
    } else if (added > 0) {
      setUnseen((c) => c + added);
    }
  }, [messages]);

  const scrollToBottom = () => {
    const box = messagesEndRef.current?.parentElement;
    if (box) box.scrollTop = box.scrollHeight;
    setUnseen(0);
  };

  const handleChatScroll = (e) => {
    const box = e.currentTarget;
    if (box.scrollHeight - box.scrollTop - box.clientHeight < 40) setUnseen(0);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setLoading(true);
    try {
      // Backend derives the receiver (artist ↔ client) — works for team members too
      await apiClient.post('/messages', {
        booking_id: booking.booking_id,
        content: newMessage
      });

      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <Card className="glass-heavy border-white/10 w-full max-w-2xl max-h-[80vh] flex flex-col">
        <CardHeader className="border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageCircle className="text-violet-400" size={24} />
              <div>
                <CardTitle>Booking Chat</CardTitle>
                <p className="text-sm text-white/60">Event: {new Date(booking.event_date).toLocaleDateString()}</p>
                <p className="text-xs text-amber-300/80">Managed chat — the RazientSounds team can see and reply here</p>
              </div>
            </div>
            <Button
              data-testid="close-chat"
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="text-white/60 hover:text-white"
            >
              <X size={20} />
            </Button>
          </div>
        </CardHeader>

        <CardContent onScroll={handleChatScroll} className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-12 text-white/40">
              <MessageCircle size={48} className="mx-auto mb-4 opacity-50" />
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isTeam = ['admin', 'owner', 'overseer'].includes(msg.sender_role);
              const isMine = msg.sender_id === user.user_id;
              if (msg.is_system) {
                return (
                  <div key={msg.message_id} className="flex justify-center" data-testid="system-message">
                    <p className="text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/25 rounded-full px-3 py-1.5 text-center max-w-[85%]">
                      {msg.content}
                    </p>
                  </div>
                );
              }
              return (
              <div
                key={msg.message_id}
                className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                    isMine
                      ? 'bg-violet-600 text-white'
                      : isTeam
                      ? 'glass-light text-white border border-amber-500/40'
                      : 'glass-light text-white'
                  }`}
                >
                  <p className="text-sm font-medium mb-1 opacity-80 flex items-center gap-1.5">
                    {msg.sender_name}
                    {isTeam && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/25 text-amber-300 border border-amber-500/40" data-testid="team-badge">TEAM</span>
                    )}
                  </p>
                  <p>{msg.content}</p>
                  <p className="text-xs opacity-60 mt-1">
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </CardContent>

        {unseen > 0 && (
          <div className="relative">
            <button
              onClick={scrollToBottom}
              data-testid="new-messages-chip"
              className="absolute -top-12 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg animate-pulse"
            >
              <ArrowDown size={14} /> {unseen} new message{unseen > 1 ? 's' : ''}
            </button>
          </div>
        )}

        <div className="p-4 border-t border-white/10">
          {isTeamUser && (
            <div className="mb-3 p-2.5 rounded-xl bg-amber-500/5 border border-amber-500/20" data-testid="team-booking-controls">
              <p className="text-[10px] font-bold uppercase tracking-widest text-amber-400/80 mb-2">Team booking controls</p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  data-testid="team-confirm-btn"
                  onClick={() => doBookingAction('confirm')}
                  className="bg-emerald-600 hover:bg-emerald-500 h-8 text-xs"
                >
                  ✅ Confirm Booking
                </Button>
                <div className="flex items-center gap-1.5">
                  <input
                    type="date"
                    data-testid="team-date-input"
                    value={actionDate}
                    onChange={(e) => setActionDate(e.target.value)}
                    className="h-8 bg-white/5 border border-white/10 rounded-lg px-2 text-xs text-white outline-none focus:border-amber-500 [color-scheme:dark]"
                  />
                  <Button
                    size="sm"
                    data-testid="team-set-date-btn"
                    disabled={!actionDate}
                    onClick={() => doBookingAction('set_date', { event_date: actionDate })}
                    className="bg-sky-600 hover:bg-sky-500 h-8 text-xs disabled:opacity-40"
                  >
                    📅 Set Date
                  </Button>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="team-cancel-btn"
                  onClick={() => { if (window.confirm('Cancel this booking?')) doBookingAction('cancel'); }}
                  className="border-red-500/50 text-red-300 hover:bg-red-500/20 h-8 text-xs"
                >
                  ✖ Cancel Booking
                </Button>
              </div>
            </div>
          )}
          <form onSubmit={handleSend} className="flex gap-2">
            <Input
              data-testid="message-input"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 bg-white/5 border-white/10 focus:border-violet-500 text-white"
              disabled={loading}
            />
            <Button
              data-testid="send-message"
              type="submit"
              disabled={loading || !newMessage.trim()}
              className="bg-violet-600 hover:bg-violet-500"
            >
              <Send size={18} />
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
